import React, { useState, useEffect } from 'react';
import { Users, Circle, Clock, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ActiveUser {
  id: string;
  full_name: string;
  profile_type: string;
  last_seen: string;
  status: 'online' | 'away' | 'offline';
}

export const ActiveUsersMonitor: React.FC = () => {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);

  useEffect(() => {
    loadActiveUsers();
    const interval = setInterval(loadActiveUsers, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadActiveUsers = async () => {
    try {
      setIsLoading(true);
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

      const { data: users, error } = await supabase
        .from('users')
        .select('id, full_name, profile_type, last_sign_in_at')
        .not('last_sign_in_at', 'is', null)
        .gte('last_sign_in_at', thirtyMinutesAgo.toISOString())
        .order('last_sign_in_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const processedUsers: ActiveUser[] = (users || []).map(user => {
        const lastSeen = new Date(user.last_sign_in_at);
        let status: 'online' | 'away' | 'offline' = 'offline';

        if (lastSeen >= fiveMinutesAgo) {
          status = 'online';
        } else if (lastSeen >= thirtyMinutesAgo) {
          status = 'away';
        }

        return {
          id: user.id,
          full_name: user.full_name || 'Utilisateur',
          profile_type: user.profile_type,
          last_seen: user.last_sign_in_at,
          status,
        };
      });

      setActiveUsers(processedUsers);
      setOnlineCount(processedUsers.filter(u => u.status === 'online').length);
    } catch (error) {
      console.error('Error loading active users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-500';
      case 'away':
        return 'text-orange-500';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online':
        return 'En ligne';
      case 'away':
        return 'Absent';
      default:
        return 'Hors ligne';
    }
  };

  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      PORTEUR: 'Porteur',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur',
      FINANCEUR: 'Financeur',
      ADMIN: 'Admin',
    };
    return labels[type] || type;
  };

  const getProfileTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      PORTEUR: 'bg-blue-100 text-blue-700',
      ACCOMPAGNATEUR: 'bg-green-100 text-green-700',
      COORDINATEUR: 'bg-purple-100 text-purple-700',
      FINANCEUR: 'bg-orange-100 text-orange-700',
      ADMIN: 'bg-red-100 text-red-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const groupedByStatus = {
    online: activeUsers.filter(u => u.status === 'online'),
    away: activeUsers.filter(u => u.status === 'away'),
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Utilisateurs connectés</h3>
              <p className="text-sm text-gray-600">
                {onlineCount} utilisateur{onlineCount !== 1 ? 's' : ''} en ligne maintenant
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-gray-600">Temps réel</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <div>
                <p className="text-sm text-green-700 font-medium">En ligne</p>
                <p className="text-2xl font-bold text-green-900">{groupedByStatus.online.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-orange-500 rounded-full" />
              <div>
                <p className="text-sm text-orange-700 font-medium">Absent</p>
                <p className="text-2xl font-bold text-orange-900">{groupedByStatus.away.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700 font-medium">Total actifs (30min)</p>
                <p className="text-2xl font-bold text-blue-900">{activeUsers.length}</p>
              </div>
            </div>
          </div>
        </div>

        {groupedByStatus.online.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Circle className="w-3 h-3 text-green-500 fill-green-500" />
              <span>En ligne maintenant</span>
            </h4>
            <div className="space-y-2">
              {groupedByStatus.online.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100 hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-700" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.full_name}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${getProfileTypeColor(user.profile_type)}`}>
                          {getProfileTypeLabel(user.profile_type)}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(user.last_seen), {
                              addSuffix: true,
                              locale: fr
                            })}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Circle className={`w-2 h-2 ${getStatusColor(user.status)} fill-current animate-pulse`} />
                    <span className={`text-sm font-medium ${getStatusColor(user.status)}`}>
                      {getStatusLabel(user.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {groupedByStatus.away.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Circle className="w-3 h-3 text-orange-500 fill-orange-500" />
              <span>Récemment actifs</span>
            </h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {groupedByStatus.away.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.full_name}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${getProfileTypeColor(user.profile_type)}`}>
                          {getProfileTypeLabel(user.profile_type)}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(user.last_seen), {
                              addSuffix: true,
                              locale: fr
                            })}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Circle className={`w-2 h-2 ${getStatusColor(user.status)} fill-current`} />
                    <span className={`text-sm font-medium ${getStatusColor(user.status)}`}>
                      {getStatusLabel(user.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Aucun utilisateur actif</p>
            <p className="text-sm">Personne n'est connecté en ce moment</p>
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-100 text-xs text-gray-600">
        <div className="flex items-center justify-between">
          <span>Actualisation automatique toutes les 10 secondes</span>
          <span>Dernière mise à jour : {new Date().toLocaleTimeString('fr-FR')}</span>
        </div>
      </div>
    </div>
  );
};
